import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, peso, fecha, hoy } from '../utils/formato';
import { saldoDe, abonosDe, proponerDescuentos, cuantasFaltan,
         detalleDelPrestamo } from '../utils/descuento';
import Comprobante from './Comprobante';
import Relacion from './Relacion';
import { periodoDe, DIAS, DIAS_DE_QUINCENA, cortesDeQuincena,
         FRECUENCIAS } from '../utils/periodo';
import { filtrarFincas } from '../utils/buscar';

// Las fincas que venden la leche, y lo que se les debe.
//
// El saldo no se guarda en la ficha: se calcula sumando entregas menos pagos
// cada vez que se pide. Un saldo guardado se desincroniza el primer día que
// alguien corrija un recibo viejo, y a partir de ahí nadie sabe cuál de los dos
// números es el bueno.
// Las dos facturas. Van aparte porque el cliente no las mezcla: a la finca que
// le vende las dos se le paga la leche de vaca en una y la de búfala en otra.
const LECHES = [
  { id: 'vaca',   ico: '🐄', nombre: 'vaca',   saldo: (f) => f.saldo_vaca ?? f.saldo },
  { id: 'bufala', ico: '🐃', nombre: 'búfala', saldo: (f) => f.saldo_bufala ?? 0 },
];

function Fincas() {
  const [cuentas, setCuentas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [nueva, setNueva] = useState(null);
  const [pagando, setPagando] = useState(null);
  const [prestamos, setPrestamos] = useState([]);
  // Los abonos de todos los préstamos. El saldo NO viene del servidor: se
  // calcula restándolos, con la misma función que usan las pruebas.
  const [abonos, setAbonos] = useState([]);
  // El historial de reliquidaciones. Va a la relación para que el dueño de la
  // finca vea por qué el precio de su semana cambió, en vez de descubrirlo
  // como una cifra que no cuadra.
  const [cambios, setCambios] = useState([]);
  // Lo que quedó debiéndose de semanas YA liquidadas. Ver el aviso de abajo.
  const [pendiente, setPendiente] = useState(null);
  // Si los préstamos no cargan, la pantalla TIENE que decirlo. Antes el fallo
  // se tragaba en silencio y el bloque simplemente no aparecía: idéntico a «no
  // le ha prestado a nadie». Una pantalla que calla un error está mintiendo,
  // y el que la mira sale a pagarle la leche completa a quien le debe plata.
  const [falloPrestamos, setFalloPrestamos] = useState(false);
  const [prestando, setPrestando] = useState(null);
  const [editando, setEditando] = useState(null);          // la ficha de la finca
  const [corrigiendo, setCorrigiendo] = useState(null);    // un préstamo
  const [guardando, setGuardando] = useState(false);
  const [comprobante, setComprobante] = useState(null);
  const [busca, setBusca] = useState('');
  const [relacion, setRelacion] = useState(null);
  // Las rutas, para poder decir a cuál pertenece cada finca. De ahí sale la
  // ruta que se propone sola al anotarle una entrega.
  const [rutas, setRutas] = useState([]);
  const [deduciendo, setDeduciendo] = useState(null);
  // Reliquidar un periodo a otro precio. Va en dos pasos: primero se ve qué
  // cambiaría, después se aplica. Esto mueve plata de recibos ya hechos.
  const [reliq, setReliq] = useState(null);
  const [vistaPrevia, setVistaPrevia] = useState(null);
  // Las archivadas se piden aparte y solo cuando alguien las pide: son las que
  // ya no se usan, y tenerlas siempre a la vista devolvería el ruido que
  // archivar vino a quitar.
  const [archivadas, setArchivadas] = useState(null);

  const cargar = useCallback(() => {
    setCargando(true);
    axios.get('/api/fincas/estado-cuenta')
      .then(r => setCuentas(r.data || []))
      .catch(() => setError('No se pudieron cargar las fincas'))
      .finally(() => setCargando(false));
    axios.get('/api/fincas/pendiente-de-cerrados')
      .then(r => setPendiente(r.data || null))
      .catch(() => setPendiente(null));
    axios.get('/api/recibos-leche/cambios-precio')
      .then(r => setCambios(r.data || []))
      .catch(() => setCambios([]));
    axios.get('/api/prestamos-fincas/abonos')
      .then(r => setAbonos(r.data || []))
      .catch(() => setAbonos([]));
    axios.get('/api/prestamos-fincas')
      .then(r => { setPrestamos(r.data || []); setFalloPrestamos(false); })
      .catch(() => setFalloPrestamos(true));
    axios.get('/api/centros').then(r => setRutas(r.data || [])).catch(() => setRutas([]));
  }, []);

  const verLasArchivadas = () =>
    axios.get('/api/fincas/estado-cuenta?archivadas=1')
      .then(r => setArchivadas(r.data || []))
      .catch(() => setArchivadas([]));

  // Los que todavía no se le han descontado a esta finca.
  // Vivo = le queda SALDO. Antes era «sin pago_id», y con las cuotas eso deja
  // fuera al que ya se saldó a punta de abonos pero cuyo marcado no llegó.
  const prestamosDe = (fincaId) =>
    prestamos.filter(p => p.finca_id === fincaId && !p.pago_id
                          && saldoDe(p, abonos) > 0);

  // ── LOS PRÉSTAMOS QUE VAN EN LA HOJA DE UN PERIODO ────────────────────────
  //
  // NO son los mismos que los vivos, y ahí estaba el error.
  //
  // A PELLO LARA se le descontaron $200.000 de los $213.400 de su semana. Ese
  // descuento saldó el préstamo, así que dejó de estar vivo — y desapareció de
  // su propia hoja. El papel decía «TOTAL A PAGAR $213.400» cuando el hombre
  // recibió $13.400.
  //
  // Un papel que dice que se le pagó diez veces lo que se le puso en la mano es
  // la peor clase de error: el finquero lo guarda, y tres meses después vuelve
  // con él.
  //
  // Van los dos montones:
  //   · Los que todavía deben — la hoja los nombra y, con cuota, los descuenta.
  //   · Y LOS QUE SE SALDARON EN ESTE MISMO PERIODO, que ya no están vivos pero
  //     son justo los que explican por qué se le pagó menos.
  //
  // Los de una fecha POSTERIOR al periodo no entran: la hoja del 12 al 18 no
  // puede hablar de un préstamo del 25.
  const prestamosDeLaRelacion = (fincaId, desde, hasta) => {
    const enElPeriodo = (a) => {
      const d = String(a.fecha || '').slice(0, 10);
      return d >= desde && d <= hasta;
    };
    return prestamos.filter(p => {
      if (p.finca_id !== fincaId) return false;
      if (String(p.fecha || '').slice(0, 10) > hasta) return false;
      if (!p.pago_id && saldoDe(p, abonos) > 0) return true;
      return abonosDe(p, abonos).some(enElPeriodo);
    });
  };

  useEffect(cargar, [cargar]);

  // ARCHIVAR, NO BORRAR.
  //
  // «Hay momentos que ya la finca no se le compra más leche y desea ser
  // eliminada» — pero borrándola de verdad, sus entregas y sus pagos se van con
  // ella: la relación quincenal de meses pasados y los papeles del contador
  // quedan con renglones sin dueño. Archivada, desaparece de la lista y de los
  // desplegables —que es lo que él quiere— y la historia queda entera.
  const archivar = async (f) => {
    if (!window.confirm(
      `¿Archivar ${f.nombre}?\n\n` +
      'Sale de la lista y de la casilla de anotar leche, así que ya no se le ' +
      'puede anotar una entrega por equivocación.\n\n' +
      'Sus entregas y sus pagos NO se borran: siguen contando en los informes ' +
      'y en las cuentas de los meses pasados.\n\n' +
      'Si vuelve a venderle, se reactiva con un botón.')) return;
    try {
      const r = await axios.delete(`/api/fincas/${f.id}`);
      if (r.data && r.data.borrada) {
        alert(`${f.nombre} se borró. No tenía ninguna entrega ni pago anotado.`);
      }
      cargar();
      if (archivadas !== null) verLasArchivadas();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo archivar');
    }
  };

  // DEDUCIR LAS RUTAS DE UNA VEZ.
  //
  // Son 93 fincas. Asignarlas a mano es una tarde, y una tarde de trabajo es
  // una función que no se usa: se deja «para después» y el programa se queda
  // a medias para siempre.
  //
  // El dato ya está: cada entrega guardó a qué ruta llegó. La ruta de una
  // finca es a dónde ha llegado su leche casi siempre. Se enseña lo que hizo
  // para poder revisarlo, en vez de cambiar 93 fichas en silencio.
  // Qué pasaría si se reliquida. NO toca nada.
  const verQueCambia = async () => {
    try {
      const { data } = await axios.get('/api/recibos-leche/reliquidar', {
        params: {
          desde: reliq.desde, hasta: reliq.hasta,
          tipo_leche: reliq.tipo_leche,
          finca_id: reliq.finca_id || undefined,
          precio: parseFloat(reliq.precio) || 0,
        },
      });
      setVistaPrevia(data);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo calcular');
    }
  };

  const aplicarReliquidacion = async () => {
    const p = parseFloat(reliq.precio) || 0;
    if (!(p > 0)) return alert('El precio debe ser mayor que cero');
    if (!window.confirm(
      `Se le va a cambiar el precio a ${vistaPrevia.recibos} entregas.\n\n` +
      `Valían ${plata(vistaPrevia.total_antes)} y van a valer ` +
      `${plata(vistaPrevia.total_despues)}.\n\n` +
      (vistaPrevia.diferencia >= 0
        ? `Va a quedar debiéndoles ${plata(vistaPrevia.diferencia)} más.`
        : `Va a quedar debiéndoles ${plata(-vistaPrevia.diferencia)} menos.`) +
      '\n\nEsto queda registrado con su nombre y la fecha.')) return;
    setGuardando(true);
    try {
      await axios.post('/api/recibos-leche/reliquidar', {
        desde: reliq.desde, hasta: reliq.hasta,
        tipo_leche: reliq.tipo_leche,
        finca_id: reliq.finca_id || null,
        precio: p, nota: reliq.nota || null,
      });
      setReliq(null); setVistaPrevia(null);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo reliquidar');
    }
    setGuardando(false);
  };

  const deducirRutas = async () => {
    if (!window.confirm(
      'El programa va a mirar a dónde ha llegado la leche de cada finca y le ' +
      'va a poner esa ruta.\n\n' +
      'Solo toca las que NO tienen ruta puesta: lo que usted haya corregido a ' +
      'mano no se pisa.\n\n' +
      'Después puede revisar una por una y cambiar las que estén mal.')) return;
    try {
      const { data } = await axios.post('/api/fincas/deducir-rutas');
      setDeduciendo(data);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudieron deducir');
    }
  };

  const reactivar = async (f) => {
    try {
      await axios.post(`/api/fincas/${f.id}/activar`);
      cargar();
      verLasArchivadas();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo activar');
    }
  };

  const crear = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      await axios.post('/api/fincas', {
        ...nueva,
        precio_litro: parseFloat(nueva.precio_litro) || 0,
        precio_litro_bufala: parseFloat(nueva.precio_litro_bufala) || null,
        propia: nueva.propia ? 1 : 0,
        precio_referencia: parseFloat(nueva.precio_referencia) || null,
        semana_empieza: nueva.semana_empieza ?? 1,
        quincena_empieza: nueva.quincena_empieza ?? 1,
        ciclo_desde: nueva.ciclo_desde || null
      });
      setNueva(null);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo guardar');
    }
    setGuardando(false);
  };

  const pagar = async (e) => {
    e.preventDefault();
    const monto = parseFloat(pagando.monto);
    if (!(monto > 0)) return alert('El monto debe ser mayor que cero');

    setGuardando(true);
    try {
      // Se manda el BRUTO —la leche— y cuáles préstamos descontar. El neto lo
      // calcula el servidor sumando los préstamos de verdad: si lo mandara la
      // pantalla, un error de tecleo se convertiría en plata.
      const { data } = await axios.post('/api/pagos-fincas/liquidar', {
        finca_id: pagando.finca.id,
        fecha: pagando.fecha,
        tipo_leche: pagando.tipoLeche || 'vaca',
        bruto: monto,
        // Se manda CUÁNTO de cada uno, no solo cuáles. El servidor lo topa
        // por el saldo de verdad: si lo tecleado fuera de más, no se cobra.
        prestamos: pagando.descontar,
        nota: pagando.nota || null
      });
      // El comprobante en vez del aviso. Decía lo mismo y se cerraba de un
      // clic: el finquero se iba sin papel y quedaba la palabra de cada uno.
      const f = pagando.finca;
      const cual = pagando.tipoLeche === 'bufala' ? 'búfala' : 'vaca';
      const descontados = (pagando.descontar || [])
        .map(d => {
          const p = prestamos.find(x => String(x.id) === String(d.id));
          // El renglón del comprobante dice lo que se descontó HOY, no el
          // monto del préstamo: al finquero se le entregó una cifra y el papel
          // tiene que poder explicarla renglón por renglón.
          //
          // Pero el detalle de abajo necesita EL DEL PRÉSTAMO, así que se
          // guarda aparte: pisándolo, el papel decía «se le prestaron $100.000»
          // de un préstamo de $500.000.
          return p ? { ...p, monto: d.monto, prestado: p.monto } : null;
        })
        .filter(Boolean);
      setComprobante({
        serie: 'PAG',
        numero: data.id,
        fecha: pagando.fecha,
        aQuien: f.propietario || f.nombre,
        documento: f.documento || null,
        monto: data.neto,
        // La factura dice de qué leche es: las dos van por separado y el papel
        // tiene que poder emparejarse con la suya.
        concepto: `Leche de ${cual} — ${f.nombre}` +
                  (pagando.nota ? ` · ${pagando.nota}` : ''),
        forma: 'efectivo',
        renglones: [
          { texto: `Leche de ${cual} entregada`, monto: data.bruto },
          // Lo mismo que dice la relación, en el comprobante. Es el papel que
          // se FIRMA al recibir la plata, y los dos papeles del mismo pago
          // tienen que decir lo mismo — si no, quien los compare pregunta.
          ...descontados.flatMap(p => {
            const d = detalleDelPrestamo({ id: p.id, monto: p.prestado },
                                         abonos, p.monto);
            return [
              { texto: `Menos préstamo del ${fecha(p.fecha)}` +
                       (p.nota ? ` — ${p.nota}` : ''),
                monto: p.monto, signo: '-' },
              { chico: true, monto: 0,
                texto: d.saldado
                  ? `Se le prestaron ${plata(d.monto)} — con esto queda a paz y salvo`
                  : `Se le prestaron ${plata(d.monto)} · abonado ${plata(d.abonado)}` +
                    ` — le quedan ${plata(d.queda)}` },
            ];
          }),
        ],
      });
      setPagando(null);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo guardar el pago');
    }
    setGuardando(false);
  };

  const prestar = async (e) => {
    e.preventDefault();
    const monto = parseFloat(prestando.monto);
    if (!(monto > 0)) return alert('El monto debe ser mayor que cero');
    setGuardando(true);
    try {
      await axios.post('/api/prestamos-fincas', {
        finca_id: prestando.finca.id,
        fecha: prestando.fecha,
        monto,
        cuota: parseFloat(prestando.cuota) || null,
        nota: prestando.nota || null,
      });
      setPrestando(null);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo guardar');
    }
    setGuardando(false);
  };

  const guardarFinca = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      await axios.put(`/api/fincas/${editando.id}`, {
        nombre: editando.nombre,
        propietario: editando.propietario || null,
        documento: editando.documento || null,
        precio_litro_bufala: parseFloat(editando.precio_litro_bufala) || null,
        propia: editando.propia ? 1 : 0,
        precio_referencia: parseFloat(editando.precio_referencia) || null,
        telefono: editando.telefono || null,
        precio_litro: parseFloat(editando.precio_litro) || 0,
        frecuencia_pago: editando.frecuencia_pago,
        centro_id: editando.centro_id ? parseInt(editando.centro_id) : null,
        semana_empieza: editando.semana_empieza ?? 1,
        quincena_empieza: editando.quincena_empieza ?? 1,
        ciclo_desde: editando.ciclo_desde || null,
      });
      setEditando(null);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo guardar');
    }
    setGuardando(false);
  };

  const guardarPrestamo = async (e) => {
    e.preventDefault();
    const monto = parseFloat(corrigiendo.monto);
    if (!(monto > 0)) return alert('El monto debe ser mayor que cero');
    setGuardando(true);
    try {
      await axios.put(`/api/prestamos-fincas/${corrigiendo.id}`, {
        monto, fecha: corrigiendo.fecha, nota: corrigiendo.nota || null,
      });
      setCorrigiendo(null);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo corregir');
    }
    setGuardando(false);
  };

  const borrarPrestamo = async (p) => {
    if (!window.confirm(`¿Borrar el préstamo de ${plata(p.monto)} del ${fecha(p.fecha)}?`)) return;
    try {
      await axios.delete(`/api/prestamos-fincas/${p.id}`);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo borrar');
    }
  };

  // La relación se arma con los recibos de ESE periodo, que se piden en el
  // momento: traerlos todos al cargar la pantalla sería bajar meses de recibos
  // de todas las fincas para imprimir uno.
  // Se abre en EL periodo de esa finca, no en el mes: hay fincas que cobran el
  // sábado y otras el 15 y el 30. Abriendo siempre en el mes, quien liquida por
  // semana corrige dos fechas cada vez, y el día que se le olvide paga de más.
  const abrirRelacion = async (f, periodo) => {
    const p = periodo || periodoDe(f.frecuencia_pago || 'mensual', hoy(), f.semana_empieza,
                              f.quincena_empieza, f.ciclo_desde);
    try {
      const { data } = await axios.get('/api/reportes/compras-leche',
        { params: { desde: p.desde, hasta: p.hasta } });
      setRelacion({
        finca: f, desde: p.desde, hasta: p.hasta,
        recibos: (data || []).filter(r => r.finca_id === f.id),
        prestamos: prestamosDeLaRelacion(f.id, p.desde, p.hasta),
        // Los cambios de precio, para poder explicar en el papel por qué la
        // leche de esa semana vale distinto de lo que se le pagó.
        cambios,
        // Los abonos van con ellos: la hoja tiene que poder decir cuánto lleva
        // abonado de cada préstamo y cuánto le queda, no solo cuánto se le
        // prestó.
        abonos,
      });
    } catch (err) {
      alert('No se pudo armar la relación. Recargue la página; si sigue igual, avise.');
    }
  };

  // El total de arriba es de TODAS, no de las filtradas: es «cuánto debo en
  // total», y que cambie al escribir en el buscador lo volvería otra cosa.
  const deudaTotal = cuentas.reduce((s, f) => s + (f.saldo || 0), 0);
  const alaVista = filtrarFincas(cuentas, busca);

  return (
    <>
      <Comprobante v={comprobante} alCerrar={() => setComprobante(null)} />
      <Relacion r={relacion} alCerrar={() => setRelacion(null)}
                alCambiarPeriodo={(p) => relacion && abrirRelacion(relacion.finca, p)} />
      {/* ── LO QUE QUEDÓ DEBIÉNDOSE DE SEMANAS YA PAGADAS ─────────────────
          
          «En el próximo pago quedaron 17.900. Quiero que el programa pueda
          avisarme que tengo que pagárselo al cliente.»
          
          Pasa al reliquidar un periodo DESPUÉS de haberlo pagado: la leche de
          esa semana pasa a valer más y el pago ya se hizo con la cifra vieja.
          
          En el estado de cuenta esa diferencia se mezcla con lo normal de la
          semana que corre, así que no se distingue — y un finquero que dejó de
          entregar leche se queda con ese saldo para siempre. A Niño Bermúdez lo
          pilló él preguntando; los demás no van a preguntar.
          
          AVISA, NO PAGA. Un programa que le suma esa diferencia al próximo pago
          por su cuenta mueve plata sin que nadie la vea, y eso es peor que el
          problema que vino a resolver. */}
      {pendiente && pendiente.fincas.length > 0 && (
        <div className="aviso ojo" style={{ marginBottom: 16 }}>
          <strong>
            Hay {plata(pendiente.total)} de leche ya entregada que quedó sin
            pagar, de semanas que ya se liquidaron.
          </strong>
          <div style={{ marginTop: 6, fontSize: 13 }}>
            Pasa cuando se le cambia el precio a una semana después de haberla
            pagado. Se le abona en el siguiente pago — pero si la finca dejó de
            entregar leche, hay que pagárselo aparte.
          </div>
          <div style={{ marginTop: 10 }}>
            {pendiente.fincas.filter(f => f.pendiente > 0).map(f => (
              <div key={f.finca_id} style={{ fontSize: 13, padding: '4px 0',
                                             borderTop: '1px solid var(--raya)' }}>
                <strong>{f.nombre}</strong>: {plata(f.pendiente)}
                {f.periodos.filter(x => x.diferencia > 0).map((x, n) => (
                  <span key={n} style={{ color: 'var(--tinta-tenue)' }}>
                    {' · '}semana del {fecha(x.desde)}: su leche valió{' '}
                    {plata(x.leche)} y se le pagaron {plata(x.liquidado)}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="encabezado">
        <h1>Fincas</h1>
        <p>Quién le vende la leche y cuánto se le debe a cada una</p>
      </div>

      {error && <div className="aviso malo">{error}</div>}

      <div className="rejilla">
        <div className="dato" style={{ '--color': 'var(--leche)' }}>
          <div className="rotulo">📤 Debe en total</div>
          <div className="valor cifra">{plata(deudaTotal)}</div>
          <div className="pie">a {cuentas.length} {cuentas.length === 1 ? 'finca' : 'fincas'}</div>
        </div>
      </div>


      {falloPrestamos && (
        <div className="aviso malo">
          <strong>No se pudieron cargar los préstamos.</strong> Lo que les prestó
          a las fincas no se está mostrando — no quiere decir que no haya.
          Recargue la página; si sigue igual, avise.
        </div>
      )}

      {/* EL LISTADO DE LO PRESTADO.
          Estaba solo dentro del cuadro de pagarle a cada finca — para saber a
          quién le debía plata había que abrir finca por finca. El total salía
          en el Panel, pero un total no sirve para cobrar: hay que saber a
          quién, cuánto y desde cuándo.

          Va ordenado del más viejo al más nuevo. El que lleva más tiempo sin
          descontarse es el que hay que mirar — no el más grande. */}
      {/* VIVO = LE QUEDA SALDO, no «sin pago_id».

          `pago_id` solo se marca cuando el préstamo llega a CERO, así que uno
          a medio descontar lo tiene en nulo y salía aquí con su monto entero
          —el mismo motivo por el que el total mentía—. */}
      {prestamos.filter(p => saldoDe(p, abonos) > 0).length > 0 && (() => {
        const pendientes = prestamos
          .filter(p => saldoDe(p, abonos) > 0)
          .map(p => {
            const f = cuentas.find(c => c.id === p.finca_id);
            const dias = Math.floor(
              (new Date(new Date().toDateString())
               - new Date(String(p.fecha).slice(0, 10) + 'T00:00:00')) / 86400000);
            return { ...p, finca: f, dias };
          })
          .sort((a, b) => b.dias - a.dias);
        // EL SALDO, NO EL MONTO.
        //
        // Sumaba lo prestado, y con las cuotas eso infla la cartera: un
        // préstamo de $2.200.000 con $2.000.000 ya descontados contaba entero.
        // Sobre la base de Gabriel eran ~$69 millones donde de verdad hay
        // ~$57,5 — once millones de diferencia en el número con el que decide
        // si puede prestar más.
        const total = pendientes.reduce((s, p) => s + saldoDe(p, abonos), 0);
        return (
          <div className="bloque">
            <h2>
              💵 Lo que les presté
              <span className="contador">
                {pendientes.length} sin descontar · {plata(total)}
              </span>
            </h2>
            <div className="tabla-caja">
              <table className="datos">
                <thead>
                  <tr>
                    <th>Finca</th>
                    <th>Cuándo</th>
                    <th style={{ textAlign: 'right' }}>Monto</th>
                    <th>Para qué</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {pendientes.map(p => (
                    <tr key={p.id}>
                      <td className="recio">
                        {p.finca ? p.finca.nombre : '—'}
                        {p.finca && p.finca.propietario && (
                          <div style={{ fontSize: 12, fontWeight: 400,
                                        color: 'var(--tinta-tenue)' }}>
                            {p.finca.propietario}
                          </div>
                        )}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {fecha(p.fecha)}
                        {/* Los días que lleva afuera, que es lo que decide si
                            hay que acordarse de descontarlo. Una fecha sola
                            obliga a restar. */}
                        <div style={{ fontSize: 12, fontWeight: 600,
                                      color: p.dias > 60 ? 'var(--malo)'
                                           : p.dias > 30 ? 'var(--ojo)'
                                           : 'var(--tinta-tenue)' }}>
                          {p.dias === 0 ? 'hoy'
                            : p.dias === 1 ? 'hace 1 día'
                            : `hace ${p.dias} días`}
                        </div>
                      </td>
                      <td className="num recio">{plata(p.monto)}</td>
                      <td style={{ color: 'var(--tinta-tenue)', fontSize: 13 }}>
                        {p.nota || '—'}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {p.finca && p.finca.saldo > 0 && (
                          <button className="boton chico" style={{ marginRight: 4 }}
                                  onClick={() => {
                                    // Se descuenta de la factura de la leche que
                                    // más le deben: es de donde alcanza.
                                    const b = p.finca.saldo_bufala || 0;
                                    const v = p.finca.saldo_vaca ?? p.finca.saldo;
                                    const cual = b > v ? 'bufala' : 'vaca';
                                    setPagando({
                                      finca: p.finca, fecha: hoy(), tipoLeche: cual,
                                      debe: Math.max(b, v),
                                      monto: String(Math.round(Math.max(b, v))), nota: '',
                                      // Marcados con LA CUOTA que toca, no con
                                      // el préstamo entero: es lo que se pactó
                                      // descontar cada vez.
                                      descontar: proponerDescuentos(
                                        prestamosDe(p.finca.id), abonos, Math.max(b, v))
                                        .filter(x => x.propuesto > 0)
                                        .map(x => ({ id: x.id, monto: x.propuesto })),
                                    });
                                  }}>
                            Descontar al pagar
                          </button>
                        )}
                        <button className="boton chico suave" title="Corregir"
                                style={{ marginRight: 4 }}
                                onClick={() => setCorrigiendo({
                                  id: p.id, monto: String(Math.round(p.monto)),
                                  fecha: String(p.fecha).slice(0, 10), nota: p.nota || '',
                                })}>
                          ✏️
                        </button>
                        <button className="boton chico suave" title="Borrar"
                                onClick={() => borrarPrestamo(p)}>
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="cuerpo" style={{ paddingTop: 10 }}>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--tinta-tenue)' }}>
                Esta plata no está perdida: se descuenta cuando se le pague la
                leche a esa finca. Los que ya se descontaron salen de esta lista
                y quedan amarrados al pago en el que fueron.
              </p>
            </div>
          </div>
        );
      })()}

      {/* Solo cuando de verdad falta: al que ya las tiene todas puestas este
          botón no le dice nada y sobra. */}
      {rutas.length > 1 && cuentas.some(f => !f.centro_id) && (
        <div className="aviso ojo" style={{ display: 'flex', alignItems: 'center',
                                            gap: 12, flexWrap: 'wrap' }}>
          <span style={{ flex: 1, minWidth: 240 }}>
            <strong>{cuentas.filter(f => !f.centro_id).length} fincas sin ruta.</strong>{' '}
            Con la ruta puesta, al anotarles una entrega se llena sola en vez de
            escogerla a mano.
          </span>
          <button className="boton chico" onClick={deducirRutas}>
            Deducirlas de la historia
          </button>
        </div>
      )}

      {deduciendo && (
        <div className="bloque">
          <h2>
            ✅ {deduciendo.asignadas} {deduciendo.asignadas === 1 ? 'finca' : 'fincas'} con ruta
            <button className="boton chico suave" style={{ marginLeft: 'auto' }}
                    onClick={() => setDeduciendo(null)}>Cerrar</button>
          </h2>
          <div className="cuerpo">
            {deduciendo.asignadas === 0 ? (
              <div className="vacio">
                Ninguna se pudo deducir: sus entregas no tienen ruta anotada.
                Toca ponerlas a mano en la ficha de cada finca.
              </div>
            ) : (
              <>
                <p style={{ fontSize: 14, color: 'var(--tinta-tenue)', marginTop: 0 }}>
                  Revíselas. La que esté mal se corrige con el ✏️ de su fila.
                </p>
                <div className="tabla-caja">
                  <table className="datos">
                    <thead>
                      <tr><th>Finca</th><th>Ruta</th>
                        <th style={{ textAlign: 'right' }}>Entregas que lo dicen</th></tr>
                    </thead>
                    <tbody>
                      {deduciendo.fincas.map((f, i) => (
                        <tr key={i}>
                          <td className="recio">{f.finca}</td>
                          <td>{f.ruta}</td>
                          <td className="num">{f.veces}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* RELIQUIDAR UN PERIODO.
          
          «Cambiar el precio de la leche con fecha desde-hasta y que ese cambio
          se vea en las facturas.» El precio de la quincena se acuerda al final,
          así que la leche entra a un precio provisional.
          
          En DOS pasos a propósito: esto cambia plata de recibos ya hechos, a
          veces cientos de una vez. Nadie debería tocar ese botón sin ver antes
          cuántos son y en cuánto queda cada finca. */}
      {!reliq ? (
        <div style={{ marginBottom: 14 }}>
          <button className="boton chico suave"
                  onClick={() => setReliq({
                    desde: hoy(), hasta: hoy(), tipo_leche: 'vaca',
                    finca_id: '', precio: '', nota: '' })}>
            💲 Cambiar el precio de un periodo
          </button>
        </div>
      ) : (
        <div className="bloque">
          <h2>
            💲 Cambiar el precio de la leche
            <button className="boton chico suave" style={{ marginLeft: 'auto' }}
                    onClick={() => { setReliq(null); setVistaPrevia(null); }}>
              Cerrar
            </button>
          </h2>
          <div className="cuerpo">
            <div className="aviso">
              Esto le cambia el precio a las entregas <strong>ya anotadas</strong> de
              ese periodo y vuelve a calcular su valor. Es lo que se hace cuando el
              precio de la quincena se acuerda al final. Queda registrado.
            </div>
            <div className="campos">
              <label className="campo">
                <span>Desde</span>
                <input type="date" value={reliq.desde} max={reliq.hasta}
                       onChange={e => { setReliq({ ...reliq, desde: e.target.value }); setVistaPrevia(null); }} />
              </label>
              <label className="campo">
                <span>Hasta</span>
                <input type="date" value={reliq.hasta} max={hoy()}
                       onChange={e => { setReliq({ ...reliq, hasta: e.target.value }); setVistaPrevia(null); }} />
              </label>
              <label className="campo">
                <span>¿Cuál leche?</span>
                <select value={reliq.tipo_leche}
                        onChange={e => { setReliq({ ...reliq, tipo_leche: e.target.value }); setVistaPrevia(null); }}>
                  <option value="vaca">🐄 Vaca</option>
                  <option value="bufala">🐃 Búfala</option>
                </select>
              </label>
              {/* Una finca o todas. Casi siempre es todas —el precio de la
                  quincena es el mismo para el que vende— pero a veces se le
                  pacta distinto a una sola. */}
              <label className="campo">
                <span>¿A cuáles fincas?</span>
                <select value={reliq.finca_id}
                        onChange={e => { setReliq({ ...reliq, finca_id: e.target.value }); setVistaPrevia(null); }}>
                  <option value="">Todas</option>
                  {cuentas.map(f => (
                    <option key={f.id} value={f.id}>{f.nombre}</option>
                  ))}
                </select>
              </label>
              <label className="campo">
                <span>Precio nuevo por litro *</span>
                <input type="number" step="1" min="0" className="peso"
                       value={reliq.precio} placeholder="0"
                       onChange={e => { setReliq({ ...reliq, precio: e.target.value }); setVistaPrevia(null); }} />
              </label>
              <label className="campo">
                <span>Por qué</span>
                <input value={reliq.nota} placeholder="Opcional — queda en el registro"
                       onChange={e => setReliq({ ...reliq, nota: e.target.value })} />
              </label>
            </div>

            {!vistaPrevia ? (
              <div className="acciones">
                <button className="boton"
                        disabled={!(parseFloat(reliq.precio) > 0)}
                        onClick={verQueCambia}>
                  Ver qué cambia
                </button>
              </div>
            ) : vistaPrevia.recibos === 0 ? (
              <div className="aviso ojo" style={{ marginBottom: 0 }}>
                No hay entregas de esa leche en ese periodo. Revise las fechas.
              </div>
            ) : (
              <>
                <div className={'aviso ' + (vistaPrevia.diferencia >= 0 ? 'ojo' : 'bueno')}>
                  <strong>{vistaPrevia.recibos} entregas</strong> ·{' '}
                  {peso(vistaPrevia.litros, 'L')} · valían{' '}
                  <strong>{plata(vistaPrevia.total_antes)}</strong> y van a valer{' '}
                  <strong>{plata(vistaPrevia.total_despues)}</strong>.
                  <div style={{ marginTop: 6 }}>
                    {vistaPrevia.diferencia >= 0
                      ? <>Va a quedar debiéndoles <strong>{plata(vistaPrevia.diferencia)} más</strong>.</>
                      : <>Va a quedar debiéndoles <strong>{plata(-vistaPrevia.diferencia)} menos</strong>.</>}
                  </div>
                  {/* Si el periodo tenía VARIOS precios, reliquidar los aplana
                      todos a uno. Puede ser lo que se quiere, o puede ser que
                      el rango se pasó de la raya. */}
                  {vistaPrevia.precios_actuales.length > 1 && (
                    <div style={{ marginTop: 6 }}>
                      Ojo: esas entregas tienen{' '}
                      <strong>{vistaPrevia.precios_actuales.length} precios distintos</strong>
                      {' '}({vistaPrevia.precios_actuales.map(p => plata(p)).join(', ')}).
                      Todas van a quedar en el precio nuevo.
                    </div>
                  )}
                </div>

                {/* Y si ya se le pagó ese periodo. No está prohibido —si el
                    precio subió, se le quedó debiendo— pero tiene que decirse:
                    el saldo se mueve DESPUÉS de haberle pagado. */}
                {vistaPrevia.pagos_en_el_periodo?.pagos > 0 && (
                  <div className="aviso malo">
                    <strong>Ese periodo ya tiene {vistaPrevia.pagos_en_el_periodo.pagos}{' '}
                    {vistaPrevia.pagos_en_el_periodo.pagos === 1 ? 'pago' : 'pagos'} hechos</strong>
                    {' '}por {plata(vistaPrevia.pagos_en_el_periodo.monto)}. Si cambia el
                    precio ahora, el saldo de esas fincas se mueve después de
                    haberles pagado — les va a quedar debiendo o pagado de más.
                  </div>
                )}

                <div className="tabla-caja">
                  <table className="datos">
                    <thead>
                      <tr><th>Finca</th>
                        <th style={{ textAlign: 'right' }}>Litros</th>
                        <th style={{ textAlign: 'right' }}>Valía</th>
                        <th style={{ textAlign: 'right' }}>Va a valer</th>
                        <th style={{ textAlign: 'right' }}>Diferencia</th></tr>
                    </thead>
                    <tbody>
                      {vistaPrevia.fincas.map((f, i) => (
                        <tr key={i}>
                          <td className="recio">{f.finca}</td>
                          <td className="num">{peso(f.litros, 'L')}</td>
                          <td className="num">{plata(f.antes)}</td>
                          <td className="num recio">{plata(f.despues)}</td>
                          <td className="num" style={{
                            color: f.diferencia > 0 ? 'var(--ojo)'
                                 : f.diferencia < 0 ? 'var(--leche)' : 'var(--tinta-tenue)' }}>
                            {f.diferencia === 0 ? '—'
                              : (f.diferencia > 0 ? '+' : '−') + plata(Math.abs(f.diferencia)).slice(1)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="acciones">
                  <button className="boton suave"
                          onClick={() => setVistaPrevia(null)}>Volver</button>
                  <button className="boton" disabled={guardando}
                          onClick={aplicarReliquidacion}>
                    {guardando ? 'Cambiando…' : '💲 Aplicar el precio nuevo'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="bloque">
        <h2>
          🚜 Estado de cuenta
          <span className="contador">
            {busca && alaVista.length !== cuentas.length
              ? `${alaVista.length} de ${cuentas.length}`
              : `${cuentas.length} activas`}
          </span>
          <button className="boton chico" style={{ marginLeft: 'auto' }}
                  onClick={() => setNueva({ nombre: '', propietario: '', documento: '', telefono: '',
                                            precio_litro_bufala: '', semana_empieza: 1, quincena_empieza: 1,
                                            propia: 0, precio_referencia: '',
                                            precio_litro: '', frecuencia_pago: 'quincenal' })}>
            ➕ Nueva finca
          </button>
        </h2>

        {/* El buscador. SIEMPRE que haya fincas.
            
            Lo puse primero a partir de seis, con el argumento de que con tres
            una caja de búsqueda es un cajón vacío. El argumento era mío y
            estaba mal: Carlos la pidió, tenía menos de seis, y no le apareció.
            Una función que se esconde por un umbral que solo yo conozco es una
            función que no está.

            Busca por finca, dueño, cédula y teléfono a la vez, porque quien
            escribe «esperanza» no sabe si eso es el nombre de la finca o el
            apellido del dueño — ni tiene por qué saberlo. */}
        {cuentas.length > 0 && (
          <div className="cuerpo" style={{ paddingBottom: 0 }}>
            <input
              value={busca}
              onChange={e => setBusca(e.target.value)}
              placeholder="Buscar por finca, dueño o cédula"
              style={{ width: '100%', boxSizing: 'border-box', padding: '11px 14px',
                       fontSize: 16, borderRadius: 8, border: '1px solid var(--linea)' }} />
          </div>
        )}

        {cargando ? (
          <div className="vacio">Cargando…</div>
        ) : cuentas.length > 0 && alaVista.length === 0 ? (
          /* Buscó y no hay nada NO es lo mismo que no tener fincas. Con el
             mismo mensaje, quien busca mal cree que se le borraron todas. */
          <div className="vacio">
            Ninguna finca con «{busca}». Pruebe con parte del nombre, del dueño
            o de la cédula — no hace falta escribirlo completo ni con tildes.
          </div>
        ) : cuentas.length === 0 ? (
          <div className="vacio">
            Todavía no hay fincas. Agrega la primera para poder recibir leche.
          </div>
        ) : (
          <div className="tabla-caja">
            <table className="datos">
              <thead>
                <tr>
                  <th>Finca</th>
                  <th>Precio</th>
                  <th>Paga</th>
                  <th style={{ textAlign: 'right' }}>Leche</th>
                  <th style={{ textAlign: 'right' }}>Entregado</th>
                  <th style={{ textAlign: 'right' }}>Pagado</th>
                  <th style={{ textAlign: 'right' }}>Debe</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {alaVista.map(f => (
                  <tr key={f.id}>
                    <td className="recio">
                      {f.nombre}
                      {f.propietario && (
                        <div style={{ fontSize: 12, color: 'var(--tinta-tenue)', fontWeight: 400 }}>
                          {f.propietario}
                        </div>
                      )}
                      {/* Se ve desde la lista, sin abrir nada: es la razón por
                          la que el pago de esta finca no es el saldo a secas. */}
                      {f.prestado_pendiente > 0 && (
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ojo)' }}>
                          💵 le prestamos {plata(f.prestado_pendiente)}
                        </div>
                      )}
                    </td>
                    <td className="num">{plata(f.precio_litro)}/L</td>
                    <td>
                      <span className="pinta gris">{f.frecuencia_pago}</span>
                    </td>
                    <td className="num">{peso(f.litros, 'L')}</td>
                    <td className="num">{plata(f.entregado)}</td>
                    <td className="num">{plata(f.pagado)}</td>
                    <td className="num recio" style={{ color: f.saldo > 0 ? 'var(--malo)' : 'var(--bueno)' }}>
                      {plata(f.saldo)}
                      {/* Partido por leche cuando vende las dos. El total sigue
                          arriba porque «cuánto le debo en total» es una
                          pregunta buena; pero se paga por separado, y un solo
                          número no deja ver cuál de las dos falta. */}
                      {f.saldo_bufala > 0 && f.saldo_vaca > 0 && (
                        <div style={{ fontSize: 11, fontWeight: 400,
                                      color: 'var(--tinta-tenue)' }}>
                          🐄 {plata(f.saldo_vaca)} · 🐃 {plata(f.saldo_bufala)}
                        </div>
                      )}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {/* Un botón POR LECHE. El cliente no mezcla las facturas:
                          a quien le vende las dos se le paga la de vaca en una
                          y la de búfala en otra. Con un solo botón «Pagar» por
                          el total, el programa estaría empujando justo a lo que
                          él pidió que no se hiciera. */}
                      {LECHES.map(({ id, ico, nombre, saldo }) => {
                        const debe = saldo(f);
                        if (debe <= 0) return null;
                        const sola = !(f.saldo_vaca > 0 && f.saldo_bufala > 0);
                        return (
                          <button key={id} className="boton chico suave"
                                  style={{ marginRight: 5 }}
                                  onClick={() => setPagando({
                                    finca: f, fecha: hoy(), tipoLeche: id, debe,
                                    monto: String(Math.round(debe)), nota: '',
                                    // Vienen marcados: si el finquero debe plata,
                                    // lo normal es descontarla. Desmarcar es la
                                    // excepción, y por eso el trabajo va del lado
                                    // de la excepción y no del caso de siempre.
                                    descontar: proponerDescuentos(
                                      prestamosDe(f.id), abonos, debe)
                                      .filter(x => x.propuesto > 0)
                                      .map(x => ({ id: x.id, monto: x.propuesto })),
                                  })}>
                            {sola ? 'Pagar' : `Pagar ${ico} ${nombre}`}
                          </button>
                        );
                      })}
                      {/* La relación día por día: el papel que revisa el
                          finquero ANTES de cobrar. Va aparte del comprobante
                          porque son dos cosas — esta es el detalle, el
                          comprobante es el recibo que se firma. */}
                      <button className="boton chico suave" style={{ marginRight: 5 }}
                              onClick={() => abrirRelacion(f)}>
                        🧾 Relación
                      </button>
                      <button className="boton chico suave" style={{ marginRight: 5 }}
                              onClick={() => setPrestando({ finca: f, fecha: hoy(),
                                                            monto: '', cuota: '', nota: '' })}>
                        Prestarle
                      </button>
                      <button className="boton chico suave" title="Corregir la ficha"
                              onClick={() => setEditando({
                                id: f.id, nombre: f.nombre,
                                propietario: f.propietario || '',
                                documento: f.documento || '',
                                precio_litro_bufala: f.precio_litro_bufala || '',
                                propia: f.propia || 0,
                                precio_referencia: f.precio_referencia || '',
                                semana_empieza: f.semana_empieza ?? 1,
                                quincena_empieza: f.quincena_empieza ?? 1,
                                ciclo_desde: f.ciclo_desde || '',
                                telefono: f.telefono || '',
                                precio_litro: String(Math.round(f.precio_litro || 0)),
                                frecuencia_pago: f.frecuencia_pago || 'quincenal',
                                centro_id: f.centro_id ? String(f.centro_id) : '',
                              })}>
                        ✏️
                      </button>
                      {/* Dice «Archivar», no «Borrar»: es exactamente lo que
                          hace, y quien lo lee ya sabe que la historia no se va. */}
                      <button className="boton chico suave" title="Archivar la finca"
                              style={{ marginLeft: 5 }}
                              onClick={() => archivar(f)}>
                        🗄️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* LAS ARCHIVADAS.
            
            Escondidas, pero no perdidas. Esto es lo que separa archivar de
            borrar: si el que archiva no puede encontrarlas después, para él es
            exactamente lo mismo que si se hubieran borrado —y la próxima vez
            que esa finca vuelva a vender, va a crearla de nuevo y la historia
            va a quedar partida en dos fichas. */}
        {!cargando && (
          <div className="cuerpo" style={{ paddingTop: 0 }}>
            {archivadas === null ? (
              <button className="boton chico suave" onClick={verLasArchivadas}>
                🗄️ Ver las fincas archivadas
              </button>
            ) : (
              <div style={{ border: '1px solid var(--linea)', borderRadius: 8,
                            padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10,
                              marginBottom: archivadas.length ? 10 : 0 }}>
                  <strong style={{ fontSize: 14 }}>🗄️ Archivadas</strong>
                  <span style={{ fontSize: 13, color: 'var(--tinta-tenue)' }}>
                    {archivadas.length === 0
                      ? 'Ninguna por ahora.'
                      : 'No aparecen en la lista ni al anotar leche. Su historia sí cuenta en los informes.'}
                  </span>
                  <button className="boton chico suave" style={{ marginLeft: 'auto' }}
                          onClick={() => setArchivadas(null)}>
                    Ocultar
                  </button>
                </div>
                {archivadas.map(f => (
                  <div key={f.id}
                       style={{ display: 'flex', alignItems: 'center', gap: 10,
                                padding: '7px 0', borderTop: '1px solid var(--linea)' }}>
                    <span style={{ fontWeight: 600 }}>{f.nombre}</span>
                    <span style={{ fontSize: 12, color: 'var(--tinta-tenue)' }}>
                      {peso(f.litros, 'L')} entregados · {plata(f.entregado)}
                    </span>
                    <button className="boton chico suave" style={{ marginLeft: 'auto' }}
                            onClick={() => reactivar(f)}>
                      Volver a activarla
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Nueva finca */}
      {nueva && (
        <div className="bloque">
          <h2>➕ Nueva finca</h2>
          <form className="cuerpo" onSubmit={crear}>
            {/* Qué leches vende no se pregunta con un selector: se dice
                poniéndole precio. Hay que decirlo, porque quien busca «tipo de
                leche» no lo encuentra — la casilla del precio es la que lo
                contesta. */}
            <div className="aviso">
              <strong>¿Qué leche le compra a esta finca?</strong> Póngale precio
              a la que le venda. Si le vende las dos, llene los dos precios; si
              solo vaca, deje el de búfala vacío.
            </div>
            <div className="campos">
              <label className="campo">
                <span>Nombre de la finca *</span>
                <input value={nueva.nombre} required
                       onChange={e => setNueva({ ...nueva, nombre: e.target.value })} />
              </label>
              <label className="campo">
                <span>Propietario</span>
                <input value={nueva.propietario}
                       onChange={e => setNueva({ ...nueva, propietario: e.target.value })} />
              </label>
              {/* La cédula. La columna existía desde el principio y el
                  formulario nunca la pidió, así que no había una sola finca
                  con cédula guardada — y el buscador por cédula no habría
                  encontrado nunca a un finquero. */}
              <label className="campo">
                <span>Cédula o NIT</span>
                <input value={nueva.documento} placeholder="Para poder buscarlo"
                       onChange={e => setNueva({ ...nueva, documento: e.target.value })} />
              </label>
              <label className="campo">
                <span>Teléfono</span>
                <input value={nueva.telefono}
                       onChange={e => setNueva({ ...nueva, telefono: e.target.value })} />
              </label>
              <label className="campo">
                <span>Precio por litro — 🐄 vaca {nueva.propia ? '' : '*'}</span>
                <input type="number" step="1" className="peso" required={!nueva.propia}
                       value={nueva.precio_litro}
                       onChange={e => setNueva({ ...nueva, precio_litro: e.target.value })} />
              </label>
              {/* El precio de la búfala va aparte porque es otro precio, y hay
                  fincas que venden las dos. Dejarlo vacío quiere decir que esta
                  finca NO vende búfala: entonces no se le puede anotar una
                  entrega de búfala por error. */}
              <label className="campo">
                <span>Precio por litro — 🐃 búfala</span>
                <input type="number" step="1" min="0" className="peso"
                       value={nueva.precio_litro_bufala}
                       placeholder="Vacío = no vende búfala"
                       onChange={e => setNueva({ ...nueva, precio_litro_bufala: e.target.value })} />
              </label>
              {/* LA FINCA PROPIA: la leche del dueño.
                  
                  Marcada, no se le pide precio de compra y NO SE LE DEBE NADA:
                  el recibo entra en cero, así que no aparece en Por cobrar, ni
                  en la relación quincenal, ni en un comprobante de egreso. Uno
                  no se debe plata a sí mismo.
                  
                  Los litros SÍ cuentan igual: entran a la tina, salen en kilos
                  y se juzgan por rendimiento. Si sus propias vacas dan leche
                  floja, tiene que enterarse. */}
              <label className="campo" style={{ gridColumn: '1 / -1' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" style={{ width: 'auto' }}
                         checked={!!nueva.propia}
                         onChange={e => setNueva({ ...nueva, propia: e.target.checked ? 1 : 0 })} />
                  Es finca propia — no se le paga
                </span>
              </label>
              {!!nueva.propia && (
                <label className="campo" style={{ gridColumn: '1 / -1' }}>
                  <span>Precio de referencia por litro</span>
                  <input type="number" step="1" min="0" className="peso"
                         value={nueva.precio_referencia || ''}
                         placeholder="Lo que le costaría comprarla — opcional"
                         onChange={e => setNueva({ ...nueva, precio_referencia: e.target.value })} />
                  <small style={{ color: 'var(--tinta-tenue)' }}>
                    Solo para costear el queso, nunca para pagar. Vacío cuenta cero
                    — y entonces el kilo de esos días sale más barato de lo que fue.
                  </small>
                </label>
              )}
              <label className="campo">
                <span>Cada cuánto se le paga</span>
                <select value={nueva.frecuencia_pago}
                        onChange={e => setNueva({ ...nueva, frecuencia_pago: e.target.value })}>
                  {/* Semanal va primero porque es el trato más apretado: la
                      finca que entrega leche todos los días y cobra el sábado.
                      Van de lo más corto a lo más largo, que es como se piensa
                      al pactarlo. */}
                  {/* De la lista central, no escritas aquí.
                      
                      Estaban puestas a mano —Semanal, Quincenal, Mensual— y
                      cuando se agregó «Cada 14 días» a la lista, la pantalla de
                      clientes la leyó de ahí y ÉSTA no se enteró. Gabriel no
                      podía escogerla para una finca porque, sencillamente, no
                      estaba en el desplegable.
                      
                      La misma lista escrita en dos sitios se desincroniza el
                      día que se toca uno. */}
                  {Object.entries(FRECUENCIAS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </label>
              {/* Solo cuando se paga por semana. La semana de este cliente va de
                  SÁBADO A VIERNES: cierra el viernes porque es el día que sale
                  a pagar. Calculándola siempre de lunes a domingo, su semana
                  quedaba partida en dos y la relación le mezclaba días de la
                  semana anterior con los de esta. */}
              {nueva.frecuencia_pago === 'semanal' && (
                <label className="campo">
                  <span>¿Qué día empieza la semana?</span>
                  <select value={nueva.semana_empieza ?? 1}
                          onChange={e => setNueva({ ...nueva,
                            semana_empieza: parseInt(e.target.value) })}>
                    {DIAS.map((d, i) => (
                      <option key={i} value={i}>
                        {d.charAt(0).toUpperCase() + d.slice(1)}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {nueva.frecuencia_pago === 'quincenal' && (
                <label className="campo">
                  <span>¿Qué día empieza la quincena?</span>
                  <select value={nueva.quincena_empieza ?? 1}
                          onChange={e => setNueva({ ...nueva,
                            quincena_empieza: parseInt(e.target.value) })}>
                    {/* Se escoge UN día y el otro sale solo, quince después.
                        Pero la lista enseña LOS DOS, porque así es como se
                        habla el acuerdo: «le pago el 5 y el 20», no «mi
                        quincena empieza el 5». Con un solo número, quien busca
                        el 20 no lo encuentra y cree que no está. */}
                    {DIAS_DE_QUINCENA.map(n => {
                      const [a, b] = cortesDeQuincena(n);
                      return (
                        <option key={n} value={n}>
                          Del {a} y del {b}{n === 1 ? ' (lo de siempre)' : ''}
                        </option>
                      );
                    })}
                  </select>
                </label>
              )}
              {nueva.frecuencia_pago === 'catorcenal' && (
                <label className="campo">
                  <span>¿Desde qué día corre el ciclo?</span>
                  <input type="date" value={nueva.ciclo_desde || ''}
                         onChange={e => setNueva({ ...nueva,
                           ciclo_desde: e.target.value })} />
                  {/* Catorce días no se anclan al calendario como la semana o
                      la quincena: hay que decir desde cuándo corre el ciclo, o
                      no hay forma de saber cuál de las dos semanas toca. Se
                      pone el primer día de un periodo que ya se pagó. */}
                  <span className="ayuda">
                    El primer día de un periodo que usted ya haya pagado.
                    De ahí en adelante van de 14 en 14.
                  </span>
                </label>
              )}
            </div>
            <div className="acciones">
              <button type="button" className="boton suave" onClick={() => setNueva(null)}>
                Cancelar
              </button>
              <button className="boton" disabled={guardando}>
                {guardando ? 'Guardando…' : '💾 Guardar finca'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Pago a una finca */}
      {editando && (
        // El `id` es para el manual: el guion de las fotos recorta justo esta
        // ficha. Sin él tenía que adivinar cuál de los bloques de la pantalla
        // es, y agarraba el buscador.
        <div className="bloque" id="ficha-finca">
          <h2>✏️ Corregir la ficha de {editando.nombre}</h2>
          <form className="cuerpo" onSubmit={guardarFinca}>
            <div className="aviso">
              Cambiar el precio por litro NO toca los recibos ya hechos: cada
              recibo guarda el precio del día en que se recibió esa leche. El
              precio nuevo se usa de aquí en adelante.
            </div>
            <div className="campos">
              <label className="campo">
                <span>Nombre de la finca *</span>
                <input value={editando.nombre} required autoFocus
                       onChange={e => setEditando({ ...editando, nombre: e.target.value })} />
              </label>
              {/* LA RUTA A LA QUE PERTENECE.
                  
                  Con esto puesta, al anotarle una entrega la ruta se llena
                  sola. Antes había que escogerla a mano en cada una de las
                  setenta entregas del día, y ahí es donde se equivoca quien
                  anota — una entrega en la ruta que no es le mueve el
                  rendimiento a dos rutas a la vez. */}
              {rutas.length > 1 && (
                <label className="campo">
                  <span>¿De cuál ruta es?</span>
                  <select value={editando.centro_id || ''}
                          onChange={e => setEditando({ ...editando, centro_id: e.target.value })}>
                    <option value="">— Sin ruta —</option>
                    {rutas.map(r => (
                      <option key={r.id} value={r.id}>{r.nombre}</option>
                    ))}
                  </select>
                </label>
              )}
              <label className="campo">
                <span>Propietario</span>
                <input value={editando.propietario}
                       onChange={e => setEditando({ ...editando, propietario: e.target.value })} />
              </label>
              <label className="campo">
                <span>Cédula o NIT</span>
                <input value={editando.documento}
                       onChange={e => setEditando({ ...editando, documento: e.target.value })} />
              </label>
              <label className="campo">
                <span>Teléfono</span>
                <input value={editando.telefono}
                       onChange={e => setEditando({ ...editando, telefono: e.target.value })} />
              </label>
              <label className="campo">
                <span>Precio por litro — 🐄 vaca *</span>
                <input type="number" step="1" min="0" className="peso" required
                       value={editando.precio_litro}
                       onChange={e => setEditando({ ...editando, precio_litro: e.target.value })} />
              </label>
              <label className="campo">
                <span>Precio por litro — 🐃 búfala</span>
                <input type="number" step="1" min="0" className="peso"
                       value={editando.precio_litro_bufala}
                       placeholder="Vacío = no vende búfala"
                       onChange={e => setEditando({ ...editando, precio_litro_bufala: e.target.value })} />
              </label>
              {/* LA FINCA PROPIA: la leche del dueño.
                  
                  Marcada, no se le pide precio de compra y NO SE LE DEBE NADA:
                  el recibo entra en cero, así que no aparece en Por cobrar, ni
                  en la relación quincenal, ni en un comprobante de egreso. Uno
                  no se debe plata a sí mismo.
                  
                  Los litros SÍ cuentan igual: entran a la tina, salen en kilos
                  y se juzgan por rendimiento. Si sus propias vacas dan leche
                  floja, tiene que enterarse. */}
              <label className="campo" style={{ gridColumn: '1 / -1' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" style={{ width: 'auto' }}
                         checked={!!editando.propia}
                         onChange={e => setEditando({ ...editando, propia: e.target.checked ? 1 : 0 })} />
                  Es finca propia — no se le paga
                </span>
              </label>
              {!!editando.propia && (
                <label className="campo" style={{ gridColumn: '1 / -1' }}>
                  <span>Precio de referencia por litro</span>
                  <input type="number" step="1" min="0" className="peso"
                         value={editando.precio_referencia || ''}
                         placeholder="Lo que le costaría comprarla — opcional"
                         onChange={e => setEditando({ ...editando, precio_referencia: e.target.value })} />
                  <small style={{ color: 'var(--tinta-tenue)' }}>
                    Solo para costear el queso, nunca para pagar. Vacío cuenta cero
                    — y entonces el kilo de esos días sale más barato de lo que fue.
                  </small>
                </label>
              )}
              <label className="campo">
                <span>Cada cuánto se le paga</span>
                <select value={editando.frecuencia_pago}
                        onChange={e => setEditando({ ...editando, frecuencia_pago: e.target.value })}>
                  {/* De la lista central, no escritas aquí.
                      
                      Estaban puestas a mano —Semanal, Quincenal, Mensual— y
                      cuando se agregó «Cada 14 días» a la lista, la pantalla de
                      clientes la leyó de ahí y ÉSTA no se enteró. Gabriel no
                      podía escogerla para una finca porque, sencillamente, no
                      estaba en el desplegable.
                      
                      La misma lista escrita en dos sitios se desincroniza el
                      día que se toca uno. */}
                  {Object.entries(FRECUENCIAS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </label>
              {/* Solo cuando se paga por semana. La semana de este cliente va de
                  SÁBADO A VIERNES: cierra el viernes porque es el día que sale
                  a pagar. Calculándola siempre de lunes a domingo, su semana
                  quedaba partida en dos y la relación le mezclaba días de la
                  semana anterior con los de esta. */}
              {editando.frecuencia_pago === 'semanal' && (
                <label className="campo">
                  <span>¿Qué día empieza la semana?</span>
                  <select value={editando.semana_empieza ?? 1}
                          onChange={e => setEditando({ ...editando,
                            semana_empieza: parseInt(e.target.value) })}>
                    {DIAS.map((d, i) => (
                      <option key={i} value={i}>
                        {d.charAt(0).toUpperCase() + d.slice(1)}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {editando.frecuencia_pago === 'quincenal' && (
                <label className="campo">
                  <span>¿Qué día empieza la quincena?</span>
                  <select value={editando.quincena_empieza ?? 1}
                          onChange={e => setEditando({ ...editando,
                            quincena_empieza: parseInt(e.target.value) })}>
                    {/* Se escoge UN día y el otro sale solo, quince después.
                        Pero la lista enseña LOS DOS, porque así es como se
                        habla el acuerdo: «le pago el 5 y el 20», no «mi
                        quincena empieza el 5». Con un solo número, quien busca
                        el 20 no lo encuentra y cree que no está. */}
                    {DIAS_DE_QUINCENA.map(n => {
                      const [a, b] = cortesDeQuincena(n);
                      return (
                        <option key={n} value={n}>
                          Del {a} y del {b}{n === 1 ? ' (lo de siempre)' : ''}
                        </option>
                      );
                    })}
                  </select>
                </label>
              )}
              {editando.frecuencia_pago === 'catorcenal' && (
                <label className="campo">
                  <span>¿Desde qué día corre el ciclo?</span>
                  <input type="date" value={editando.ciclo_desde || ''}
                         onChange={e => setEditando({ ...editando,
                           ciclo_desde: e.target.value })} />
                  {/* Catorce días no se anclan al calendario como la semana o
                      la quincena: hay que decir desde cuándo corre el ciclo, o
                      no hay forma de saber cuál de las dos semanas toca. Se
                      pone el primer día de un periodo que ya se pagó. */}
                  <span className="ayuda">
                    El primer día de un periodo que usted ya haya pagado.
                    De ahí en adelante van de 14 en 14.
                  </span>
                </label>
              )}
            </div>
            <div className="acciones">
              <button type="button" className="boton suave" onClick={() => setEditando(null)}>
                Cancelar
              </button>
              <button className="boton" disabled={guardando}>💾 Guardar</button>
            </div>
          </form>
        </div>
      )}

      {corrigiendo && (
        <div className="bloque">
          <h2>✏️ Corregir el préstamo</h2>
          <form className="cuerpo" onSubmit={guardarPrestamo}>
            <div className="aviso">
              Solo se puede corregir mientras no se le haya descontado. Una vez
              descontado queda amarrado al pago, porque es la explicación de por
              qué se le entregó menos plata.
            </div>
            <div className="campos">
              <label className="campo">
                <span>Monto *</span>
                <input type="number" step="1" min="0" className="peso" required autoFocus
                       value={corrigiendo.monto}
                       onChange={e => setCorrigiendo({ ...corrigiendo, monto: e.target.value })} />
              </label>
              <label className="campo">
                <span>Fecha</span>
                <input type="date" value={corrigiendo.fecha}
                       onChange={e => setCorrigiendo({ ...corrigiendo, fecha: e.target.value })} />
              </label>
              <label className="campo">
                <span>Para qué</span>
                <input value={corrigiendo.nota} placeholder="Opcional"
                       onChange={e => setCorrigiendo({ ...corrigiendo, nota: e.target.value })} />
              </label>
            </div>
            <div className="acciones">
              <button type="button" className="boton suave" onClick={() => setCorrigiendo(null)}>
                Cancelar
              </button>
              <button className="boton" disabled={guardando}>💾 Guardar</button>
            </div>
          </form>
        </div>
      )}

      {prestando && (
        <div className="bloque">
          <h2>💵 Prestarle a {prestando.finca.nombre}</h2>
          <form className="cuerpo" onSubmit={prestar}>
            <div className="aviso">
              Esta plata se le va a descontar cuando se le pague la leche. No
              cobra interés: es un adelanto.
              {prestando.finca.prestado_pendiente > 0 && (
                <> Ya le tenemos prestado{' '}
                  <strong>{plata(prestando.finca.prestado_pendiente)}</strong>.</>
              )}
            </div>
            <div className="campos">
              <label className="campo">
                <span>Cuánto le presta *</span>
                <input type="number" step="1" className="peso" required autoFocus
                       value={prestando.monto}
                       onChange={e => setPrestando({ ...prestando, monto: e.target.value })} />
              </label>
              {/* LA CUOTA: cuánto se le descuenta cada vez.
                  
                  Sin esto el préstamo era todo o nada, y un millón de golpe
                  contra una quincena deja a la finca sin cobrar nada. Por eso
                  en la práctica se llevaba en un cuaderno aparte. */}
              <label className="campo">
                <span>¿Cuánto le descuenta cada vez?</span>
                <input type="number" step="1" min="0" className="peso"
                       value={prestando.cuota}
                       placeholder="Vacío = todo de una vez"
                       onChange={e => setPrestando({ ...prestando, cuota: e.target.value })} />
              </label>
              <label className="campo">
                <span>Fecha</span>
                <input type="date" value={prestando.fecha}
                       onChange={e => setPrestando({ ...prestando, fecha: e.target.value })} />
              </label>
              <label className="campo">
                <span>Para qué</span>
                <input value={prestando.nota} placeholder="Opcional"
                       onChange={e => setPrestando({ ...prestando, nota: e.target.value })} />
              </label>
              {/* EL PLAZO, mientras se teclea. Es lo que pregunta el dueño de
                  la finca al recibir la plata: «¿y hasta cuándo me
                  descuentan?». Sacarlo con la calculadora delante de él es
                  justo lo que este programa vino a quitar. */}
              {(() => {
                const m = parseFloat(prestando.monto) || 0;
                const c = parseFloat(prestando.cuota) || 0;
                if (!(m > 0 && c > 0)) return null;
                const n = cuantasFaltan({ monto: m, cuota: c }, []);
                return (
                  <div className="aviso" style={{ gridColumn: '1 / -1' }}>
                    Se le descuentan <strong>{plata(c)}</strong> cada vez que se
                    le pague la leche: <strong>{n} {n === 1 ? 'vez' : 'veces'}</strong>
                    {' '}para quedar en cero.
                    {m % c !== 0 && <> La última sería de {plata(m - c * (n - 1))}.</>}
                  </div>
                );
              })()}
            </div>
            <div className="acciones">
              <button type="button" className="boton suave" onClick={() => setPrestando(null)}>
                Cancelar
              </button>
              <button className="boton" disabled={guardando}>
                {guardando ? 'Guardando…' : '💾 Anotar el préstamo'}
              </button>
            </div>
          </form>
        </div>
      )}

      {pagando && (
        <div className="bloque">
          <h2>
            💵 Pagarle a {pagando.finca.nombre}
            {pagando.tipoLeche === 'bufala' ? ' — 🐃 búfala' : ' — 🐄 vaca'}
          </h2>
          <form className="cuerpo" onSubmit={pagar}>
            <div className="aviso">
              Debe <strong>{plata(pagando.debe ?? pagando.finca.saldo)}</strong> de
              leche de {pagando.tipoLeche === 'bufala' ? 'búfala' : 'vaca'}.
              {pagando.finca.ultimo_pago &&
                <> Último pago el {fecha(pagando.finca.ultimo_pago)}.</>}
            </div>

            {/* Los préstamos, con su check.
                EL CHECK ES TODO EL DISEÑO: marcado se descuenta y el préstamo
                queda saldado; sin marcar se le paga completo y sigue vivo para
                la próxima. El programa propone, la persona confirma — uno que
                descuenta sin preguntar se gana una desconfianza que no se
                recupera, y con el dueño de una finca eso cuesta la leche. */}
            {prestamosDe(pagando.finca.id).length > 0 && (() => {
              const suyos = prestamosDe(pagando.finca.id);
              const bruto = parseFloat(pagando.monto) || 0;

              // LO QUE EL PROGRAMA PROPONE DESCONTAR: la cuota pactada, topada
              // por el saldo y por lo que se le está pagando. Ese último tope
              // es el que evita que la finca salga a deber por haber entregado
              // leche la semana que entregó poco.
              const propuestas = proponerDescuentos(suyos, abonos, bruto);
              const propuestaDe = (id) => propuestas.find(x => String(x.id) === String(id));

              // Lo tecleado manda sobre lo propuesto: quien paga puede bajarlo
              // o saltarlo, porque ese trato se negocia en la puerta.
              const elegido = (id) => {
                const puesto = (pagando.descontar || []).find(d => String(d.id) === String(id));
                return puesto ? Math.max(0, Number(puesto.monto) || 0) : 0;
              };
              const descuento = suyos.reduce((t, p) => t + elegido(p.id), 0);

              const poner = (id, monto) => {
                const otros = (pagando.descontar || []).filter(d => String(d.id) !== String(id));
                const m = Math.max(0, Math.round(Number(monto) || 0));
                setPagando({ ...pagando,
                  descontar: m > 0 ? [...otros, { id, monto: m }] : otros });
              };
              // Al marcar a mano se pone `alMarcar` y no `propuesto`.
              //
              // Un préstamo sin cuota ya no se propone solo —nadie lo
              // autorizó— así que su `propuesto` es cero. Usándolo aquí, marcar
              // la casilla pondría cero y no pasaría nada: el tilde no haría
              // nada y quien paga no sabría por qué.
              const alternar = (id) => {
                const pr = propuestaDe(id);
                poner(id, elegido(id) > 0 ? 0 : (pr ? pr.alMarcar : 0));
              };
              return (
                <div style={{ border: '1px solid var(--linea)', borderRadius: 8,
                              padding: '12px 14px', marginBottom: 14 }}>
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>
                    Le prestamos plata. ¿Se la descontamos?
                  </div>
                  {suyos.map(p => {
                    const pr = propuestaDe(p.id) || { saldo: saldoDe(p, abonos), propuesto: 0 };
                    const yaAbonado = abonosDe(p, abonos);
                    const puesto = elegido(p.id);
                    return (
                    <label key={p.id}
                           style={{ display: 'flex', alignItems: 'baseline', gap: 10,
                                    padding: '6px 0', cursor: 'pointer', flexWrap: 'wrap' }}>
                      <input type="checkbox" style={{ width: 18, height: 18 }}
                             checked={puesto > 0}
                             onChange={() => alternar(p.id)} />
                      <span style={{ minWidth: 110 }}>{fecha(p.fecha)}</span>
                      {/* EL SALDO, no el monto. Con las cuotas hay préstamos a
                          medias, y enseñar el monto haría creer que se le va a
                          descontar lo que ya abonó. */}
                      <span className="cifra recio">{plata(pr.saldo)}</span>
                      {yaAbonado.length > 0 && (
                        <span style={{ color: 'var(--tinta-tenue)', fontSize: 13 }}>
                          de {plata(p.monto)} · {yaAbonado.length}{' '}
                          {yaAbonado.length === 1 ? 'abono' : 'abonos'}
                        </span>
                      )}
                      {/* Cuánto se le descuenta HOY. Editable: el programa
                          propone la cuota y quien paga decide. */}
                      {puesto > 0 && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <span style={{ fontSize: 13, color: 'var(--tinta-tenue)' }}>hoy</span>
                          <input type="number" step="1" min="0" max={pr.saldo}
                                 className="peso" style={{ width: 120 }}
                                 value={puesto}
                                 onClick={e => e.preventDefault()}
                                 onChange={e => poner(p.id, e.target.value)} />
                        </span>
                      )}
                      {p.cuota > 0 && (
                        <span style={{ color: 'var(--tinta-tenue)', fontSize: 13 }}>
                          cuota {plata(p.cuota)}
                        </span>
                      )}
                      {pr.recortadoPorElPago && puesto > 0 && (
                        <span style={{ color: 'var(--ojo)', fontSize: 13 }}>
                          la leche no alcanza para la cuota completa
                        </span>
                      )}
                      {p.nota && (
                        <span style={{ color: 'var(--tinta-tenue)', fontSize: 13 }}>
                          {p.nota}
                        </span>
                      )}
                      {/* Se corrige aquí mismo, que es donde se descubre el
                          error: mirando la cifra antes de entregar la plata. */}
                      <span style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                        <button type="button" className="boton chico suave" title="Corregir"
                                onClick={() => setCorrigiendo({
                                  id: p.id, monto: String(Math.round(p.monto)),
                                  fecha: String(p.fecha).slice(0, 10), nota: p.nota || '',
                                })}>
                          ✏️
                        </button>
                        <button type="button" className="boton chico suave" title="Borrar"
                                onClick={() => borrarPrestamo(p)}>
                          🗑️
                        </button>
                      </span>
                    </label>
                    );
                  })}
                  {/* La resta, hecha. Que nadie tenga que hacerla de cabeza
                      con el finquero esperando al frente. */}
                  <div style={{ borderTop: '1px solid var(--linea)', marginTop: 8,
                                paddingTop: 8, display: 'flex', flexDirection: 'column',
                                gap: 3 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Leche</span>
                      <span className="cifra">{plata(bruto)}</span>
                    </div>
                    {descuento > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between',
                                    color: 'var(--ojo)' }}>
                        <span>Menos {suyos.filter(p => elegido(p.id) > 0).length}{' '}
                          {suyos.filter(p => elegido(p.id) > 0).length === 1
                            ? 'préstamo' : 'préstamos'}</span>
                        <span className="cifra">− {plata(descuento)}</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between',
                                  fontWeight: 700, fontSize: 17 }}>
                      <span>A pagarle hoy</span>
                      <span className="cifra">{plata(Math.max(0, bruto - descuento))}</span>
                    </div>
                    {/* Lo que le queda debiendo se resta del SALDO, no del
                        monto. Decía «le queda debiendo $800.000» cuando el
                        saldo era $600.000 y se le descontaban $200.000: estaba
                        restando de un millón que ya no debía, porque $400.000
                        los había abonado en las semanas anteriores. */}
                    {(() => {
                      const saldoAntes = suyos.reduce((t, p) => t + saldoDe(p, abonos), 0);
                      const queda = Math.max(0, saldoAntes - descuento);
                      if (queda <= 0) return null;
                      return (
                        <div style={{ fontSize: 13, color: 'var(--tinta-tenue)' }}>
                          Le queda debiendo {plata(queda)} de lo prestado.
                        </div>
                      );
                    })()}
                  </div>
                </div>
              );
            })()}
            <div className="campos">
              <label className="campo">
                <span>Leche de este periodo *</span>
                <input type="number" step="1" className="peso" required autoFocus
                       value={pagando.monto}
                       onChange={e => setPagando({ ...pagando, monto: e.target.value })} />
              </label>
              <label className="campo">
                <span>Fecha</span>
                <input type="date" value={pagando.fecha}
                       onChange={e => setPagando({ ...pagando, fecha: e.target.value })} />
              </label>
              <label className="campo">
                <span>Nota</span>
                <input value={pagando.nota} placeholder="Opcional"
                       onChange={e => setPagando({ ...pagando, nota: e.target.value })} />
              </label>
            </div>
            <div className="acciones">
              <button type="button" className="boton suave" onClick={() => setPagando(null)}>
                Cancelar
              </button>
              <button className="boton" disabled={guardando}>
                {guardando ? 'Guardando…' : '💾 Registrar el pago'}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

export default Fincas;
